import { ApiError, refreshToken, type TokenProvider } from "@serophin/shared";
import { AUTH_CACHE_KEYS, getAccessToken, getRefreshToken, hasRefreshSession, setAuthTokenPair, signOut } from "./auth-cache";
import { cache } from "./cache";

let inflight: Promise<string | undefined> | null = null;

// A stored token goes in the body; without one the server reads the HttpOnly refresh cookie, which the browser sends itself.
const renew = async (): Promise<string | undefined> => {
  const stored = getRefreshToken();
  try {
    const response = await refreshToken.invoke(stored === null ? {} : { refreshToken: stored }, { skipAuth: true });
    setAuthTokenPair(response);
    return response.accessToken;
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      signOut();
    }
    return undefined;
  }
};

export const ensureAccessToken = async (): Promise<string | undefined> => {
  const accessToken = getAccessToken();
  if (accessToken) {
    return accessToken;
  }
  if (!hasRefreshSession()) {
    return undefined;
  }
  if (inflight) {
    return inflight;
  }
  inflight = renew();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
};

export const renewAccessToken = (): Promise<string | undefined> => {
  if (!inflight) {
    cache.remove(AUTH_CACHE_KEYS.accessToken);
  }
  return ensureAccessToken();
};

export const authTokenProvider: TokenProvider = { get: ensureAccessToken, refresh: renewAccessToken };
